import { Axios } from "axios";
import { APIError } from "./_axios";
import { UserFast, SignatureFast, ServiceFast, AccountFast, IpadFast } from "./_models_fast";
import { Ipad, IpadSetupToken, SelectableObituary, UserAccountMembership } from "./_models";

export function getUserByToken(axios: Axios): Promise<{ user: UserFast; allowedAccountsUniqueIds: string[] }> {
    //
    // Note to future self:
    // If you change this route, search for `/authenticated/auth/me` and then
    // make sure your axios interceptor doesn't add the header for the new route path.
    //
    return axios
        .get(`/V2_API/authenticated/auth/me`, {}).then((res) => res.data).then((response: any) => {
            if (!response.data.user) {
                throw new APIError('Malformed response (missing user)', {})
            }
            if (!response.data.allowed_accounts_unique_ids || !Array.isArray(response.data.allowed_accounts_unique_ids)) {
                throw new APIError('Malformed response (missing allowed_accounts_unique_ids)', {})
            }
            return {
                user: new UserFast(response.data.user),
                allowedAccountsUniqueIds: response.data.allowed_accounts_unique_ids // just an array of strings now
            }
        })
}

export function getMyAvailableAccounts(axios: Axios): Promise<AccountFast[]> {
    return axios.get(`/V2_API/authenticated/auth/my-available-accounts`).then((res) => res.data).then((response: any) => {
        if (!response.data.accounts) {
            throw new APIError('Malformed response (missing accounts)', {})
        }
        return response.data.accounts.map((account: any) => new AccountFast(account))
    })
}

// Current account routes

export function getCurrentAccount(axios: Axios): Promise<AccountFast> {
    return axios.get(`/V2_API/authenticated/current-account/get-current-account`).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new AccountFast(response.data.account)
    })
}

export function updateCurrentAccount(axios: Axios, data: any): Promise<AccountFast> {
    return axios.post(`/V2_API/authenticated/current-account/update-current-account`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new AccountFast(response.data.account)
    })
}

export function getCurrentAccountUsers(axios: Axios): Promise<{ user_account_memberships: UserAccountMembership[]; user_account_memberships_count: number }> {
    return axios.get(`/V2_API/authenticated/current-account/get-current-account-users`).then((res) => res.data).then((response: any) => {
        if (!response.data.user_account_memberships) {
            throw new APIError('Malformed response', {})
        }
        return {
            user_account_memberships: response.data.user_account_memberships.map((userAccountMembership: any) => new UserAccountMembership(userAccountMembership)),
            user_account_memberships_count: response.data.user_account_memberships_count,
        }
    })
}

export function addUserToCurrentAccount(axios: Axios, data: any): Promise<UserFast> {
    return axios.post(`/V2_API/authenticated/current-account/add-user-to-current-account`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.user_account_membership) {
            throw new APIError('Malformed response', {})
        }
        return new UserFast(response.data.user_account_membership)
    })
}

export function createStripeCheckoutSessionForCurrentAccount(axios: Axios, data: { planId: string }): Promise<string> {
    return axios.post(`/V2_API/authenticated/current-account/create-stripe-checkout-session`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.checkout_session_client_secret) {
            throw new APIError('Malformed response', {})
        }
        return response.data.checkout_session_client_secret
    })
}

export function generateStripeBillingPortalForCurrentAccount(axios: Axios, data: any = {}): Promise<string> {
    return axios.post(`/V2_API/authenticated/current-account/generate-stripe-billing-portal`, data)
        .then((res) => res.data)
        .then((response: any) => {
            if (!response.data.billing_portal_url) {
                throw new APIError('Malformed response', {})
            }
            return response.data.billing_portal_url
        })
}

export function getObituariesThatCanBeUsedToCreateService(axios: Axios): Promise<{
    obituaries: SelectableObituary[];
    obituaries_count: number;
}> {
    return axios.get(`/V2_API/authenticated/current-account/services/get-available-obituaries`).then((res) => res.data).then((response: any) => {
        if (!response.data.obituaries) {
            throw new APIError('Malformed response', {})
        }
        // We shouldn't need to filter by latest_obituary_detail_extraction, because the backend
        // does that already. But it's extra safety.
        return {
            obituaries: response.data.obituaries.filter((x: SelectableObituary) => x.public_data.latest_obituary_detail_extraction).map((obituary: any) => new SelectableObituary(obituary)),
            obituaries_count: response.data.obituaries_count,
        }
    })
}

export function createService(axios: Axios, data: any, photoOfPersonBlob: Blob): Promise<ServiceFast> {
    const formData = new FormData();
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            // Convert boolean values to string for FormData
            const value = typeof data[key] === 'boolean' ? data[key].toString() : data[key];
            formData.append(key, value);
        }
    }
    formData.append('deceased_person_photo', photoOfPersonBlob)
    return axios.post(`/V2_API/authenticated/current-account/services/create-service`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceFast(response.data.service)
    })
}

export function addIpad(axios: Axios, data: any): Promise<IpadSetupToken> {
    return axios.post(`/V2_API/authenticated/current-account/ipads/add-ipad`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad_setup_token) {
            throw new APIError('Malformed response', {})
        }
        return new IpadSetupToken(response.data.ipad_setup_token)
    })
}

// Signatures

export function getSignatures(
    axios: Axios,
    page: number = 1,
    onlyPreneed: boolean = false,
    searchQuery: string = ''
): Promise<{ signatures: SignatureFast[]; total_signatures: number }> {
    const params = new URLSearchParams({
        page: page.toString(),
        onlyPreneed: onlyPreneed ? 'yes' : 'no',
        ...(searchQuery && { search: searchQuery })
    });
    
    return axios.get(`/V2_API/authenticated/list-of-signatures?${params}`).then((res) => res.data).then((response: any) => {
        if (!response.data.signatures) {
            throw new APIError('Malformed response (missing signatures)', {})
        }
        return {
            signatures: response.data.signatures.map((signature: any) => new SignatureFast(signature)),
            total_signatures: response.data.total_signatures,
        }
    })
}

export function getGraphOfSignaturesOverTime(axios: Axios, data?: any): Promise<{
    signatures: { date: string; count: number }[];
    preneed_count: number;
}> {
    return axios.post(`/V2_API/authenticated/graph-of-signatures`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.signatures) {
            throw new APIError('Malformed response (missing signatures)', {})
        }
        return {
            signatures: response.data.signatures,
            preneed_count: response.data.preneed_count || 0
        }
    })
}

// Signatures by account

export interface AccountBreakdown {
    account_id: number;
    account_name: string;
    account_unique_identifier: string;
    service_count: number;
    signature_count: number;
    preneed_count: number;
}

export function getSignaturesByAccount(
    axios: Axios,
    startDate?: string,
    endDate?: string,
    limit?: number
): Promise<{ accounts: AccountBreakdown[]; total_accounts: number; total_signatures: number; start_date: string; end_date: string }> {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (limit) params.set('limit', limit.toString());
    const qs = params.toString();
    return axios.get(`/V2_API/authenticated/signatures-by-account${qs ? `?${qs}` : ''}`).then((res) => res.data).then((response: any) => {
        if (!response.data.accounts) {
            throw new APIError('Malformed response (missing accounts)', {})
        }
        return {
            accounts: response.data.accounts,
            total_accounts: response.data.total_accounts || 0,
            total_signatures: response.data.total_signatures || 0,
            start_date: response.data.start_date,
            end_date: response.data.end_date,
        }
    })
}

// Services

export function getServices(axios: Axios, page: number = 1, language?: string, search?: string): Promise<{ services: ServiceFast[]; total_services: number }> {
    let url = `/V2_API/authenticated/list-of-services?page=${page}`;
    if (language) {
        url += `&language=${encodeURIComponent(language)}`;
    }
    if (search) {
        url += `&search=${encodeURIComponent(search)}`;
    }
    return axios.get(url).then((res) => res.data).then((response: any) => {
        if (!response.data.services) {
            throw new APIError('Malformed response', {})
        }
        return {
            services: response.data.services.map((service: any) => new ServiceFast(service)),
            total_services: response.data.total_services,
        }
    })
}

export function getGraphOfServicesOverTime(axios: Axios, data?: any): Promise<{
    date: string; count: number;
}[]> {
    return axios.post(`/V2_API/authenticated/graph-of-services`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.services) {
            throw new APIError('Malformed response', {})
        }
        return response.data.services
    })
}


export function getServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string): Promise<ServiceFast> {
    return axios.get(`/V2_API/authenticated/get-service?unique_id=${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceFast(response.data.service)
    })
}

export function sendServiceToIpadByUniqueIdentifier(axios: Axios, iPadUniqueIdentifier: string, serviceUniqueIdentifier: string) {
    return axios.post(`/V2_API/authenticated/send-service-to-ipad`, {
        service_unique_identifier: serviceUniqueIdentifier,
        ipad_unique_identifier: iPadUniqueIdentifier,
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad) {
            throw new APIError('Malformed response', {})
        }
        return new Ipad(response.data.ipad)
    })
}

export function updateServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any, photoOfPersonBlob: Blob | null) {
    const formData = new FormData();
    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            // Convert boolean values to string for FormData
            const value = typeof data[key] === 'boolean' ? data[key].toString() : data[key];
            formData.append(key, value);
        }
    }
    if (photoOfPersonBlob) {
        formData.append('deceased_person_photo', photoOfPersonBlob)
    }
    return axios.post(`/V2_API/authenticated/update-service?unique_identifier=${uniqueIdentifier}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceFast(response.data.service)
    })
}

// iPads

export function getIPads(
    axios: Axios,
    service_unique_id?: string
): Promise<{ ipads: IpadFast[]; total_ipads: number }> {
    const url = service_unique_id
        ? `/V2_API/authenticated/list-of-ipads?service_unique_id=${encodeURIComponent(service_unique_id)}`
        : `/V2_API/authenticated/list-of-ipads`;
    return axios.get(url).then((res) => res.data).then((response: any) => {
        if (!response.data.ipads) {
            throw new APIError('Malformed response (missing ipads)', {})
        }
        return {
            ipads: response.data.ipads.map((ipad: any) => new IpadFast(ipad)),
            total_ipads: response.data.total_ipads,
        }
    })
}
